import { createRoot } from 'react-dom/client';
import { useRef, useEffect, useState, useCallback } from 'react';
import { SandboxEditorReact, SandboxEditorHandle } from './editor/SandboxEditorReact';
import { SandboxConsole } from './console/SandboxConsole';
import { SandboxRunnerProvider, PreviewPanel, useSandboxRunner } from './runner';
import { DEFAULT_SYSTEMS, TEMPLATES } from './templates';
import { treePathOfV1Path, type ProjectTree } from '@gotti/sandbox-core';
import { exportAsGbox, exportAsStandalone, openImportDialog, createGboxDocument, projectOfDocument } from './export';
import { openSandboxFromUrl } from './persistence/openFromUrl';
import { listAssets, replaceAllAssets, subscribe as subscribeAssets } from './assets';
import { saveStateOf, useSandboxPersistence } from './persistence';
import { AgentProvider, AgentChatPanel } from './agent';
import { SandboxHeader } from './header/SandboxHeader';
import { readGottiRunbooks, writeGottiRunbooks, type GboxRunbook } from '@gotti/sandbox-shared';
import { listRunbooks, seedRunbooks, clearRunbooks } from './agent/runbookStore';
import './main.css';

/**
 * Resizable panel divider hook
 */
function useResizer() {
  const resizerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizer = resizerRef.current;
    if (!resizer) return;

    const leftPanel = document.querySelector('.editor-panel') as HTMLElement;
    const rightPanel = document.querySelector('.preview-panel') as HTMLElement;
    const container = document.querySelector('.main-container') as HTMLElement;

    if (!leftPanel || !rightPanel || !container) return;

    let isDragging = false;
    // Grab-relative drag state, captured at mousedown. We move the divider by the
    // cursor's *delta* rather than snapping the boundary onto the absolute pointer
    // position — the old absolute formula ignored the agent panel's width, so it
    // jumped the divider left of the cursor whenever the agent panel was open.
    let startX = 0;
    let startLeftWidth = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startLeftWidth = leftPanel.getBoundingClientRect().width;
      resizer.classList.add('active');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // The two panels share exactly the flex space they currently occupy;
      // measuring it live keeps us immune to the sidebar / agent-panel / border
      // terms the old absolute formula had to model (and got wrong).
      const flexSpace = leftPanel.getBoundingClientRect().width + rightPanel.getBoundingClientRect().width;
      if (flexSpace <= 0) return;

      const newLeftWidth = startLeftWidth + (e.clientX - startX);
      const leftPercent = Math.max(10, Math.min(90, (newLeftWidth / flexSpace) * 100));
      const rightPercent = 100 - leftPercent;

      leftPanel.style.flex = `${leftPercent} 1 0%`;
      rightPanel.style.flex = `${rightPercent} 1 0%`;
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        resizer.classList.remove('active');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.dispatchEvent(new Event('resize'));
      }
    };

    resizer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      resizer.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return resizerRef;
}

/**
 * Resizable panel divider hook for the agent panel
 */
function useAgentResizer(isAgentOpen: boolean) {
  const resizerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAgentOpen) return;
    
    const resizer = resizerRef.current;
    if (!resizer) return;

    const previewPanel = document.querySelector('.preview-panel') as HTMLElement;
    const agentPanel = document.querySelector('.agent-chat-panel') as HTMLElement;
    const container = document.querySelector('.main-container') as HTMLElement;

    if (!previewPanel || !agentPanel || !container) return;

    let isDragging = false;
    // Grab-relative drag state (see useResizer): resize by the cursor's delta so
    // the panel edge never snaps away from the pointer on grab.
    let startX = 0;
    let startWidth = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startWidth = agentPanel.getBoundingClientRect().width;
      resizer.classList.add('active');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Agent panel sits on the right, so dragging left (negative delta) grows it.
      const newWidth = startWidth - (e.clientX - startX);
      const clampedWidth = Math.max(280, Math.min(600, newWidth));

      agentPanel.style.width = `${clampedWidth}px`;
      agentPanel.style.flex = 'none';
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        resizer.classList.remove('active');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.dispatchEvent(new Event('resize'));
      }
    };

    resizer.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      resizer.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isAgentOpen]);

  return resizerRef;
}

/**
 * Main sandbox content - needs to be inside the provider to use the context
 */
function SandboxContent() {
  const editorRef = useRef<SandboxEditorHandle>(null);
  // Last full snapshot pushed into the editor tree (path → content), so a
  // `files:sync` (which fires before EVERY test run) only re-applies files whose
  // content actually changed — avoids editor/cursor thrash on no-op snapshots.
  const lastSyncedFilesRef = useRef<Map<string, string>>(new Map());
  const consolePanelRef = useRef<SandboxConsole | null>(null);
  const resizerRef = useResizer();
  const [isInitialized, setIsInitialized] = useState(false);
  const [sandboxName, setSandboxName] = useState('Untitled Sandbox');
  const [hasTemplate, setHasTemplate] = useState(true);
  // `unsaved`: whether the project had unsaved changes when it was reset — an undo brings them back unsaved.
  const [undoResetSnapshot, setUndoResetSnapshot] = useState<{ project: ProjectTree; name: string; runbooks: GboxRunbook[]; unsaved: boolean } | null>(null);
  const undoResetTimerRef = useRef<number | null>(null);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  
  // Agent panel state
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const agentResizerRef = useAgentResizer(isAgentOpen);

  const { run, stop, statusText, getClientSettings, restoreClients, updateClientSettings, runTestsFromFiles, syncAgentFiles, replaceAgentFiles } = useSandboxRunner();
  const [persistence, persistenceActions] = useSandboxPersistence();
  // The save state: the project is dirty after any change — the editor's (its onProjectChanged, below) and the assets' —
  // and clean after a save (the persistence hook) or a load (each loader below marks it once the editor holds it).
  const { markDirty, markClean } = persistenceActions;
  useEffect(() => subscribeAssets(markDirty), [markDirty]);

  // Try to load from URL on mount (only if backend available and URL has hash)
  useEffect(() => {
    if (!isInitialized || !persistence.isBackendAvailable) return;
    
    const loadFromUrl = async () => {
      // A document the editor cannot open is reported and the sandbox forgotten (openSandboxFromUrl), never dropped silently.
      const result = await openSandboxFromUrl({
        loadFromUrl: persistenceActions.loadFromUrl,
        open: (doc) => {
          stop();
          editorRef.current?.loadProject(projectOfDocument(doc));
        },
        detach: persistenceActions.detach,
        report: (message) => alert(message),
      });
      if (result) {
        markClean();
        seedRunbooks(readGottiRunbooks(result));
        if (result.meta?.name) {
          setSandboxName(result.meta.name);
        }
        consolePanelRef.current?.clear();
        setHasTemplate(false);
        console.info(`[Sandbox] Loaded "${result.meta?.name || 'Sandbox'}"`);
        
        // Auto-run and restore clients
        setTimeout(async () => {
          const project = editorRef.current?.getProject();
          if (project) {
            await run(project);
            
            // Restore clients if the saved doc had them
            if (result.clients && result.clients.length > 1) {
              // Top up to the saved clients: the project's definition may already boot some or all of them.
              await restoreClients(result.clients.length);
              
              // Apply saved settings to each client
              const currentSettings = getClientSettings();
              result.clients.forEach((savedClient, index) => {
                if (currentSettings[index]) {
                  updateClientSettings(currentSettings[index].id, savedClient.settings);
                }
              });
              
              console.info(`[Sandbox] Restored ${result.clients.length} clients with settings`);
            } else if (result.clients && result.clients.length === 1) {
              // Just one client, apply its settings
              const currentSettings = getClientSettings();
              if (currentSettings[0] && result.clients[0]) {
                updateClientSettings(currentSettings[0].id, result.clients[0].settings);
              }
            }
          }
        }, 100);
      }
    };
    
    loadFromUrl().catch(() => {
      // Failed to load from URL - just continue with default sandbox
      // Clear any hash from URL to avoid confusion
      clearRunbooks();
      if (window.location.pathname !== '/') {
        window.history.replaceState(null, '', '/');
      }
    });
  }, [isInitialized, persistence.isBackendAvailable]);

  // Helper to create GboxDocument from current state
  const createCurrentDocument = useCallback(() => {
    const project = editorRef.current?.getProject();
    if (!project) return null;
    
    const clientSettings = getClientSettings();
    const clients = clientSettings.map(c => ({
      id: c.id,
      displayName: c.displayName,
      settings: c.settings,
    }));
    
    return writeGottiRunbooks(
      createGboxDocument(project, {
        name: sandboxName,
        clients: clients.length > 0 ? clients : undefined,
        assets: listAssets(),
      }),
      listRunbooks(),
    );
  }, [sandboxName, getClientSettings]);

  // Handle save to backend - creates new revision if sandbox exists, otherwise new hash
  const handleSave = useCallback(async () => {
    if (!persistence.isBackendAvailable) {
      console.warn('[Sandbox] Backend not available - use Export instead');
      return;
    }

    editorRef.current?.markCodeCheckpoint();
    const gboxDoc = createCurrentDocument();
    if (!gboxDoc) return;
    
    let response;
    if (persistence.currentSandbox) {
      // Existing sandbox - create new revision
      response = await persistenceActions.saveRevision(gboxDoc);
      if (response) {
        console.info(`[Sandbox] Saved revision ${response.revision}! URL: ${window.location.href}`);
      }
    } else {
      // New sandbox
      response = await persistenceActions.save(gboxDoc);
      if (response) {
        console.info(`[Sandbox] Saved new sandbox! URL: ${window.location.href}`);
      }
    }
  }, [persistence.isBackendAvailable, persistence.currentSandbox, persistenceActions, createCurrentDocument]);

  // Handle fork - always creates new hash
  const handleFork = useCallback(async () => {
    if (!persistence.isBackendAvailable) {
      console.warn('[Sandbox] Backend not available - use Export instead');
      return;
    }
    
    const gboxDoc = createCurrentDocument();
    if (!gboxDoc) return;
    
    // Always call fork with current document
    // If it's a new sandbox or existing one, fork() (via forkWithContent) 
    // handles creating a fresh hash with the current content
    const response = await persistenceActions.fork(gboxDoc);
    if (response) {
      console.info(`[Sandbox] Forked! New URL: ${window.location.href}`);
    }
  }, [persistence.isBackendAvailable, persistenceActions, createCurrentDocument]);

  // Initialize console capture
  useEffect(() => {
    if (isInitialized) return;
    setIsInitialized(true);

    // Wait for DOM to be ready then capture console
    const consoleElement = document.getElementById('console-output');
    if (consoleElement && !consolePanelRef.current) {
      consolePanelRef.current = new SandboxConsole(consoleElement);
      consolePanelRef.current.captureConsole();
      console.info('[Sandbox] Initializing Gotti Sandbox...');
    }
  }, [isInitialized]);

  // Auto-run on first render (only if no URL hash - persistence will handle it)
  useEffect(() => {
    if (!isInitialized) return;
    
    // If there's a hash in the URL, don't auto-start - let loadFromUrl handle it
    const hasUrlHash = window.location.pathname.match(/^\/[a-z0-9]{6,12}(?:\/\d+)?$/);
    if (hasUrlHash) {
      console.info('[Sandbox] URL has hash, waiting for backend to load...');
      return;
    }

    // Give React a moment to render the editor
    const timer = setTimeout(async () => {
      console.info('[Sandbox] Auto-starting...');
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isInitialized, run]);

  // Set up file change listener for auto-restart
  useEffect(() => {
    let restartTimeout: number | null = null;

    const restartRunner = async () => {
      console.info('[Sandbox] Restarting with updated systems...');
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    };

    editorRef.current?.setOnSystemsChanged((type, folder) => {
      if (type === 'add' || type === 'delete') {
        console.info(`[Sandbox] File ${type}d in ${folder}, scheduling restart...`);

        if (restartTimeout) {
          clearTimeout(restartTimeout);
        }
        restartTimeout = window.setTimeout(() => {
          restartTimeout = null;
          restartRunner();
        }, 500);
      }
    });

    return () => {
      if (restartTimeout) clearTimeout(restartTimeout);
    };
  }, [run]);

  const handleRun = useCallback(async () => {
    console.info('[Sandbox] Running sandbox...');
    const project = editorRef.current?.getProject();
    editorRef.current?.markCodeCheckpoint();
    if (project) {
      await run(project);
    }
  }, [run]);

  const handleReset = useCallback(() => {
    // Save current state for undo
    const currentProject = editorRef.current?.getProject();
    if (currentProject) {
      setUndoResetSnapshot({ project: currentProject, name: sandboxName, runbooks: listRunbooks(), unsaved: persistence.hasUnsavedChanges });
      // Auto-dismiss undo toast after 8 seconds
      if (undoResetTimerRef.current) clearTimeout(undoResetTimerRef.current);
      undoResetTimerRef.current = window.setTimeout(() => {
        setUndoResetSnapshot(null);
        undoResetTimerRef.current = null;
      }, 8000);
    }

    console.info('[Sandbox] Resetting sandbox...');
    stop();
    editorRef.current?.resetToDefaults();
    markClean();
    consolePanelRef.current?.clear();
    // NOTE: clearing follows the existing reset semantics — persistence.currentSandbox
    // is NOT severed here (pre-existing behavior, same as systems/name), so a Save
    // after reset still writes a revision of the prior lineage, now without runbooks.
    clearRunbooks();
    const template = TEMPLATES[currentTemplateIndex];
    setSandboxName(template?.name === 'Empty' ? 'Untitled Sandbox' : template?.name ?? 'Untitled Sandbox');
    // Clear URL hash when resetting
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }

    // Auto-run with reset code (same pattern as template switching)
    setTimeout(async () => {
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    }, 100);
  }, [stop, run, sandboxName, currentTemplateIndex, persistence.hasUnsavedChanges, markClean]);

  const handleUndoReset = useCallback(() => {
    if (!undoResetSnapshot) return;
    console.info('[Sandbox] Undoing reset...');
    stop();
    editorRef.current?.loadProject(undoResetSnapshot.project);
    // The project as it was before the reset: unsaved changes it had are unsaved again.
    if (undoResetSnapshot.unsaved) markDirty(); else markClean();
    consolePanelRef.current?.clear();
    setSandboxName(undoResetSnapshot.name);
    seedRunbooks(undoResetSnapshot.runbooks);
    setUndoResetSnapshot(null);
    if (undoResetTimerRef.current) {
      clearTimeout(undoResetTimerRef.current);
      undoResetTimerRef.current = null;
    }

    // Auto-run with restored code
    setTimeout(async () => {
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    }, 100);
  }, [undoResetSnapshot, stop, run, markDirty, markClean]);

  // Handle template change
  const handleTemplateChange = useCallback((index: number) => {
    const template = TEMPLATES[index];
    if (!template) return;

    setCurrentTemplateIndex(index);
    
    console.info(`[Sandbox] Loading template: ${template.name}`);
    stop();
    editorRef.current?.loadSystems(template.systems);
    editorRef.current?.setDefaults(template.systems);
    markClean();
    consolePanelRef.current?.clear();
    clearRunbooks();
    setSandboxName(template.name);
    setHasTemplate(true);
    setUndoResetSnapshot(null);

    // Clear URL hash when switching templates
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
    
    // Auto-run with new template
    setTimeout(async () => {
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    }, 100);
  }, [stop, run, markClean]);

  // Handle creating a new sandbox from a template
  const handleCreateNewSandbox = useCallback((selectedNewTemplateIndex: number) => {
    const template = TEMPLATES[selectedNewTemplateIndex];
    if (!template) return;

    setCurrentTemplateIndex(selectedNewTemplateIndex);
    
    console.info(`[Sandbox] Creating new sandbox from template: ${template.name}`);
    stop();
    editorRef.current?.loadSystems(template.systems);
    editorRef.current?.setDefaults(template.systems);
    markClean();
    consolePanelRef.current?.clear();
    clearRunbooks();
    setSandboxName(template.name === 'Empty' ? 'Untitled Sandbox' : template.name);
    setHasTemplate(true);
    setUndoResetSnapshot(null);

    // Clear URL hash when creating new sandbox
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
    
    // Auto-run with new template
    setTimeout(async () => {
      const project = editorRef.current?.getProject();
      if (project) {
        await run(project);
      }
    }, 100);
  }, [stop, run, markClean]);

  const handleExportGbox = useCallback(async () => {
    const project = editorRef.current?.getProject();
    if (project) {
      // Get current client settings for export
      const clientSettings = getClientSettings();
      const clients = clientSettings.map(c => ({
        id: c.id,
        displayName: c.displayName,
        settings: c.settings,
      }));
      
      await exportAsGbox(project, {
        name: 'My Sandbox Project',
        clients: clients.length > 0 ? clients : undefined,
        assets: listAssets(),
        runbooks: listRunbooks(),
      });
    }
  }, [getClientSettings]);

  const handleExportStandalone = useCallback(async () => {
    const project = editorRef.current?.getProject();
    if (project) {
      await exportAsStandalone(project, listAssets());
    }
  }, []);

  // Agent integration callbacks
  const handleAgentApplyCode = useCallback((filename: string, code: string) => {
    // Strip leading slash if present (AI paths use /folder/subfolder/file.ts)
    const normalizedPath = filename.startsWith('/') ? filename.slice(1) : filename;
    console.log('[Agent] Applying code to:', normalizedPath, '(original:', filename, ')');

    // Mirror the agent file into the runner (the legacy `collectTests(systems)`
    // reads it); the project's runs and the Tests panel read the tree, which
    // updateFile below writes — root `__tests__/` specs included.
    syncAgentFiles({ [normalizedPath]: code });

    // Use the editor's updateFile method (now auto-creates missing files)
    const success = editorRef.current?.updateFile(normalizedPath, code);

    if (success) {
      console.log('[Agent] Code applied successfully to', normalizedPath);
    } else {
      console.warn('[Agent] Failed to apply code:', normalizedPath);
    }
  }, [syncAgentFiles]);

  // AUTHORITATIVE full-snapshot sync of the AI's working files (from the worker's
  // `files:sync` event). Keeps the sandbox ALWAYS in sync with whatever the AI
  // holds — even for silent bulk mutations (rollback / autofix / bootstrap-strip)
  // and deletes that the per-file `file:modified` stream can't express. Without
  // this the TESTS panel can show "No tests found" while the worker runs failing
  // specs.
  const handleSyncFiles = useCallback((files: { path: string; content: string }[]) => {
    // 1. Replace the runner mirror with EXACTLY this set — what the legacy
    //    `collectTests(systems)` reads, deletes included, which the upsert-only
    //    `syncAgentFiles` path can't express. Project runs read the tree.
    const record: Record<string, string> = {};
    for (const f of files) {
      const normalizedPath = f.path.startsWith('/') ? f.path.slice(1) : f.path;
      record[normalizedPath] = f.content;
    }
    replaceAgentFiles(record);
    // 2. Best-effort: reflect code files into the structured editor tree so the
    //    file explorer matches the AI's view — including bulk mutations
    //    (rollback/autofix) the per-file file:modified stream never emitted. Only
    //    push files whose content CHANGED since the last snapshot (this fires
    //    before every test run), so the editor doesn't thrash on no-op syncs.
    //    A root spec (`__tests__/x.spec.ts`) lands at `src/__tests__/` like any
    //    AI file; a path updateFile refuses (a generated file) is warned about.
    const prev = lastSyncedFilesRef.current;
    const next = new Map<string, string>();
    for (const [normalizedPath, content] of Object.entries(record)) {
      next.set(normalizedPath, content);
      if (prev.get(normalizedPath) === content) continue; // unchanged — skip
      try { editorRef.current?.updateFile(normalizedPath, content); } catch { /* non-structured path — lives in the mirror */ }
    }
    lastSyncedFilesRef.current = next;
  }, [replaceAgentFiles]);

  /** Every file the AI sees, at its v1 path (`client/…`, `__tests__/…`) — the project's own files, generated ones left out. */
  const getSandboxFiles = useCallback(() => editorRef.current?.getAiFiles() ?? [], []);

  /** One file the AI names by its v1 path, or null when the project has no such file the AI sees. */
  const getFileContent = useCallback((filename: string): string | null => {
    const treePath = treePathOfV1Path(filename);
    if (treePath === null) return null;
    const v1Path = treePath.slice('src/'.length);
    return editorRef.current?.getAiFiles().find((file) => file.path === v1Path)?.content ?? null;
  }, []);

  const handleImport = useCallback(async () => {
    const result = await openImportDialog();
    
    if (!result.success) {
      if (result.errors[0] !== 'Import cancelled') {
        console.error('[Sandbox] Import failed:', result.errors.join(', '));
        alert(`Import failed: ${result.errors.join(', ')}`);
      }
      return;
    }

    if (result.warnings.length > 0) {
      console.warn('[Sandbox] Import warnings:', result.warnings);
    }

    if (result.project) {
      // Stop current game
      stop();
      
      // Open the imported project in the editor
      editorRef.current?.loadProject(result.project);
      if (result.assets) replaceAllAssets(result.assets);
      seedRunbooks(result.runbooks ?? []);
      // An import is a load: what it brought in, assets included, is nothing unsaved of this session's.
      markClean();

      // Clear console
      consolePanelRef.current?.clear();
      setHasTemplate(false);
      
      console.info(`[Sandbox] Imported "${result.meta?.name || 'Sandbox Project'}"`);
      
      // Auto-run with imported systems
      setTimeout(async () => {
        const project = editorRef.current?.getProject();
        if (project) {
          await run(project);
          
          // Restore additional clients if the import had more than 1
          if (result.clients && result.clients.length > 1) {
            // Top up to the imported clients: the project's definition may already boot some or all of them.
            await restoreClients(result.clients.length);
            
            // Get the current client IDs and apply settings
            const currentSettings = getClientSettings();
            result.clients.forEach((importedClient, index) => {
              if (currentSettings[index]) {
                updateClientSettings(currentSettings[index].id, importedClient.settings);
              }
            });
            
            console.info(`[Sandbox] Restored ${result.clients.length} clients with settings`);
          } else if (result.clients && result.clients.length === 1) {
            // Just one client, apply its settings
            const currentSettings = getClientSettings();
            if (currentSettings[0] && result.clients[0]) {
              updateClientSettings(currentSettings[0].id, result.clients[0].settings);
            }
          }
        }
      }, 100);
    }
  }, [stop, run, restoreClients, getClientSettings, updateClientSettings, markClean]);

  // Update status text in footer
  useEffect(() => {
    const statusElement = document.getElementById('status-text');
    if (statusElement) {
      statusElement.textContent = statusText;
    }
  }, [statusText]);

  // Expose for testing (keep in sync with latest handle)
  useEffect(() => {
    (window as any).sandboxEditor = editorRef.current;
  });

  const content = (
    <>
      <SandboxHeader
        templates={TEMPLATES}
        currentTemplateIndex={currentTemplateIndex}
        onTemplateChange={handleTemplateChange}
        onCreateNewSandbox={handleCreateNewSandbox}
        onRun={handleRun}
        backendAvailable={persistence.isBackendAvailable}
        isSaving={persistence.isSaving}
        currentSandbox={persistence.currentSandbox}
        onSave={handleSave}
        onFork={handleFork}
        onImport={handleImport}
        onExportGbox={handleExportGbox}
        onExportStandalone={handleExportStandalone}
        showReset={hasTemplate}
        onReset={handleReset}
        agentOpen={isAgentOpen}
        onToggleAgent={() => setIsAgentOpen(!isAgentOpen)}
        saveState={saveStateOf(persistence)}
      />

      {/* Main content */}
      <main className="main-container">
        <SandboxEditorReact
          ref={editorRef}
          initialSystems={DEFAULT_SYSTEMS}
          onProjectChanged={markDirty}
        />

        <div ref={resizerRef} className="gutter" id="panel-resizer" />

        <PreviewPanel />
        
        {/* Agent Panel */}
        {isAgentOpen && (
          <>
            <div ref={agentResizerRef} className="gutter" id="agent-resizer" />
            <AgentChatPanel />
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="status-bar">
        <span className="status-item" id="status-text">Ready</span>
        <span className="status-item">Gotti Sandbox v0.1</span>
      </footer>

      {/* Undo reset toast */}
      {undoResetSnapshot && (
        <div className="undo-toast">
          <span>Reset to template defaults</span>
          <button className="undo-toast-btn" onClick={handleUndoReset}>Undo</button>
          <button className="undo-toast-dismiss" onClick={() => setUndoResetSnapshot(null)} title="Dismiss">&times;</button>
        </div>
      )}
    </>
  );
  
  // Wrap in AgentProvider with callbacks
  return (
    <AgentProvider
      onApplyCode={handleAgentApplyCode}
      getSandboxFiles={getSandboxFiles}
      getFileContent={getFileContent}
      onRunTestsFromFiles={runTestsFromFiles}
      onSyncFiles={handleSyncFiles}
    >
      {content}
    </AgentProvider>
  );
}

/**
 * Root component that wraps everything in providers
 */
function SandboxApp() {
  return (
    <SandboxRunnerProvider>
      <SandboxContent />
    </SandboxRunnerProvider>
  );
}

// Bootstrap the knowledge graph BEFORE the React tree mounts so the
// designStore subscription is in place when KnowledgeGraphPanel mounts.
// (Imported here, not at the top, so the side-effect order matches
// the comment.)
import { initKnowledgeGraph } from './knowledge/bootstrap';
initKnowledgeGraph();

// Mount the React app to the root
const appRoot = document.getElementById('app');
if (appRoot) {
  const root = createRoot(appRoot);
  // Note: StrictMode is intentionally NOT used here because Monaco editor
  // doesn't play well with React StrictMode's double-mount behavior in dev mode
  root.render(<SandboxApp />);
}
